import { h } from '../lib/dom.js';
import { fmtTime, fmtDuration, fmtDate, dayName, statusLabel, statusIcon, priorityLabel,
  fmtNet, taskTypeLabel, taskTypeIcon, sourceTypeLabel } from '../lib/format.js';
import { openModal, closeModal, toast, confirmModal } from '../lib/ui.js';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api.js';

export function statusChip(status) {
  return h('span', { class: `chip chip-status status-${status}` },
    h('span', { class: 'status-icon', 'aria-hidden': 'true' }, statusIcon(status)),
    statusLabel(status),
  );
}

export function taskRow(task, { onChange, canEdit = false, canComplete = true, compact = false } = {}) {
  const done = task.status === 'completed';
  const isBreak = !!Number(task.is_break);
  const taskType = task.task_type || 'study';
  const row = h('div', {
    class: `task-row p-${task.priority || 'normal'} ${done ? 'done' : ''}${isBreak ? ' is-break' : ''} tt-${taskType}`,
    dataset: { id: task.id },
  });

  const hasExtras = !!(task.description || task.note || task.postponed_reason || task.source || task.source_name
                      || task.actual_minutes || task.question_actual
                      || task.question_correct || task.question_incorrect || task.question_blank);
  // Compact modda gövdeye tıklayınca detay modal'ı; her zaman detay butonu da eklenir
  const bodyProps = { class: 'task-body' + (compact ? ' clickable' : '') };
  if (compact) {
    bodyProps.role = 'button';
    bodyProps.tabIndex = 0;
    bodyProps.onclick = () => openDetailModal(task, { canEdit, canComplete, onChange });
    bodyProps.onkeydown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDetailModal(task, { canEdit, canComplete, onChange });
      }
    };
  }
  row.appendChild(h('div', bodyProps,
    h('div', { class: 'task-title' },
      isBreak ? h('span', { class: 'break-icon', 'aria-hidden': 'true' }, '☕') : null,
      task.title || (isBreak ? 'Mola' : ''),
      hasExtras && !isBreak ? h('span', { class: 'extras-dot', title: 'Ek detay var' }) : null,
    ),
    h('div', { class: 'task-meta' },
      isBreak
        ? h('span', { class: 'break-chip' }, 'Mola')
        : (task.subject_name ? h('span', {
            class: 'subject-tag',
            style: { '--subject-color': task.subject_color || 'var(--color-border-strong)' },
          }, task.subject_name) : null),
      !isBreak && taskType !== 'study' ? h('span', {
        class: 'task-type-badge tt-' + taskType,
        title: taskTypeLabel(taskType),
      }, taskTypeIcon(taskType), ' ', taskTypeLabel(taskType)) : null,
      !isBreak && task.topic_names ? h('span', { class: 'muted' }, '· ' + task.topic_names)
        : (!isBreak && task.topic_name ? h('span', { class: 'muted' }, '· ' + task.topic_name) : null),
      task.planned_start ? h('span', { class: 'muted' },
        '· ' + fmtTime(task.planned_start) + (task.planned_end ? '–' + fmtTime(task.planned_end) : '')
      ) : null,
      task.estimated_minutes ? h('span', { class: 'muted' }, '· ' + fmtDuration(task.estimated_minutes)) : null,
      !isBreak && task.question_target ? h('span', { class: 'muted' }, '· ' + task.question_target + ' soru') : null,
      !isBreak ? statusChip(task.status) : null,
    ),
  ));

  const actions = h('div', { class: 'task-actions' });
  const mkBtn = (label, iconLabel, cls, opts) =>
    h('button', {
      ...opts,
      class: 'btn ' + cls + ' btn-sm',
      title: compact ? label : (opts.title || ''),
      'aria-label': compact ? label : null,
    }, compact ? iconLabel : label);

  // Detay butonu — molalarda gerekli değil
  if (!isBreak) {
    actions.appendChild(mkBtn('Detay', 'ⓘ', 'btn-ghost', {
      onclick: () => openDetailModal(task, { canEdit, canComplete, onChange }),
    }));
  }

  if (canComplete && !done && task.status !== 'postponed' && !isBreak) {
    actions.appendChild(mkBtn('Tamamla', '✓', 'btn-secondary', {
      onclick: () => openCompleteModal(task, onChange),
    }));
  }
  if (canEdit && done && !isBreak) {
    actions.appendChild(mkBtn('Aktiviteyi düzenle', '✎+', 'btn-secondary', {
      onclick: () => openCompleteModal(task, onChange),
      title: 'Tamamlama detaylarını düzenle',
    }));
  }
  if (canEdit) {
    actions.appendChild(mkBtn('Düzenle', '✎', 'btn-ghost', {
      onclick: () => openTaskModal(task, onChange),
    }));
    actions.appendChild(mkBtn('Ertele', '⤴', 'btn-ghost', {
      onclick: () => openPostponeModal(task, onChange),
      title: 'Ertele',
    }));
    actions.appendChild(mkBtn('Arşivle', '✕', 'btn-danger', {
      onclick: () => confirmModal({
        title: 'Görevi arşivle',
        message: '"' + task.title + '" arşivlenecek. Geçmiş veriler korunur.',
        confirmText: 'Arşivle', danger: true,
        onConfirm: async () => {
          await apiDelete('/tasks/' + task.id);
          toast('Görev arşivlendi');
          onChange && onChange();
        }
      }),
      title: 'Arşivle',
    }));
  }
  row.appendChild(actions);
  return row;
}

// ==== Break (mola) modal — kompakt, tek amaçlı ====
export function openBreakModal(existing, onChange, defaults = {}) {
  const isEdit = !!existing;
  const b = existing || {};
  const title = h('input', { class: 'input', type: 'text', maxlength: 200,
    value: b.title || 'Mola', placeholder: 'Mola' });
  const dateIn = h('input', { class: 'input', type: 'date', required: true,
    value: b.task_date ?? defaults.task_date ?? '' });
  const sIn = h('input', { class: 'input', type: 'time',
    value: b.planned_start ? b.planned_start.slice(0, 5) : (defaults.planned_start || '') });
  const eIn = h('input', { class: 'input', type: 'time',
    value: b.planned_end ? b.planned_end.slice(0, 5) : '' });
  const dur = h('input', { class: 'input', type: 'number', min: 1, max: 240,
    value: b.estimated_minutes ?? 15 });
  const note = h('textarea', { class: 'textarea', placeholder: 'Neden bu mola? (opsiyonel)' },
    b.description || '');

  // Süre değişince otomatik bitiş saati (başlangıç varsa)
  const syncEnd = () => {
    if (!sIn.value || !dur.value) return;
    const [hh, mm] = sIn.value.split(':').map(Number);
    const total = hh * 60 + mm + Number(dur.value);
    const h2 = String(Math.floor(total / 60) % 24).padStart(2, '0');
    const m2 = String(total % 60).padStart(2, '0');
    eIn.value = `${h2}:${m2}`;
  };
  sIn.addEventListener('change', syncEnd);
  dur.addEventListener('change', syncEnd);
  // Yeni mola oluşturulurken önerilen başlangıç varsa bitişi otomatik doldur
  if (!isEdit && sIn.value && dur.value && !eIn.value) syncEnd();

  // Hızlı süre butonları
  const quickBtn = (min) => h('button', {
    type: 'button', class: 'btn btn-ghost btn-sm',
    onclick: () => { dur.value = min; syncEnd(); },
  }, min + ' dk');

  const save = h('button', { class: 'btn btn-primary',
    onclick: async () => {
      save.disabled = true;
      try {
        const payload = {
          is_break: true,
          title: title.value.trim() || 'Mola',
          task_date: dateIn.value,
          planned_start: sIn.value || null,
          planned_end: eIn.value || null,
          estimated_minutes: dur.value === '' ? null : Number(dur.value),
          description: note.value || null,
        };
        if (isEdit) await apiPatch('/tasks/' + b.id, payload);
        else { payload.week_id = defaults.week_id; await apiPost('/tasks', payload); }
        toast(isEdit ? 'Mola güncellendi' : 'Mola eklendi', { type: 'success' });
        closeModal();
        onChange && onChange();
      } catch (err) {
        toast(err.message || 'Kaydedilemedi', { type: 'error' });
        save.disabled = false;
      }
    },
  }, isEdit ? 'Kaydet' : 'Molayı ekle');

  openModal({
    title: isEdit ? '☕ Molayı düzenle' : '☕ Yeni mola',
    body: h('div', { class: 'stack' },
      h('div', { class: 'field' }, h('label', {}, 'Başlık'), title),
      h('div', { class: 'form-grid' },
        h('div', { class: 'field' }, h('label', {}, 'Tarih'), dateIn),
        h('div', { class: 'field' }, h('label', {}, 'Süre (dk)'), dur),
        h('div', { class: 'field' }, h('label', {}, 'Başlangıç'), sIn),
        h('div', { class: 'field' }, h('label', {}, 'Bitiş'), eIn),
      ),
      h('div', { class: 'row row-wrap', style: { marginTop: '-4px' } },
        h('span', { class: 'subtle' }, 'Hızlı:'),
        quickBtn(5), quickBtn(10), quickBtn(15), quickBtn(20), quickBtn(30),
      ),
      h('div', { class: 'field' }, h('label', {}, 'Not'), note),
    ),
    actions: [
      h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'),
      save,
    ],
  });
}

// ==== Task detail modal — tüm alanları okunur biçimde gösterir ====
export function openDetailModal(task, { canEdit = false, canComplete = true, onChange } = {}) {
  const done = task.status === 'completed';
  const topicNames = task.topic_names
    || (task.topic_name ? task.topic_name : null);

  const kv = (label, value) => value == null || value === '' ? null
    : h('div', { class: 'detail-kv' },
        h('span', { class: 'detail-key' }, label),
        h('span', { class: 'detail-val' }, value),
      );

  const timeRange = task.planned_start
    ? fmtTime(task.planned_start) + (task.planned_end ? ' – ' + fmtTime(task.planned_end) : '')
    : null;

  const actualTimeRange = (task.actual_start || task.actual_end)
    ? [task.actual_start && new Date(task.actual_start).toLocaleTimeString('tr-TR', {hour:'2-digit',minute:'2-digit'}),
       task.actual_end   && new Date(task.actual_end).toLocaleTimeString('tr-TR', {hour:'2-digit',minute:'2-digit'})]
       .filter(Boolean).join(' – ')
    : null;

  const taskType = task.task_type || 'study';
  const sourceLine = task.source_name
    ? (sourceTypeLabel(task.source_type) + ' · ' + task.source_name)
    : (task.source || null);
  const grid = h('div', { class: 'detail-grid' },
    kv('Tür', taskTypeLabel(taskType)),
    kv('Ders', task.subject_name),
    kv('Konular', topicNames),
    kv('Tarih', dayName(task.task_date) + ', ' + fmtDate(task.task_date)),
    kv('Öncelik', priorityLabel(task.priority)),
    kv('Planlanan saat', timeRange),
    kv('Planlanan süre', task.estimated_minutes ? fmtDuration(task.estimated_minutes) : null),
    kv('Gerçekleşen süre', task.actual_minutes ? fmtDuration(task.actual_minutes) : null),
    kv('Gerçek saat aralığı', actualTimeRange),
    kv('Soru hedefi', task.question_target),
    kv('Çözülen soru', task.question_actual),
    kv('Doğru', task.question_correct),
    kv('Yanlış', task.question_incorrect),
    kv('Boş', task.question_blank),
    (task.question_correct != null || task.question_incorrect != null)
      ? kv('Net (D − 0.25·Y)', fmtNet(task.question_correct, task.question_incorrect))
      : null,
    kv('Kaynak', sourceLine),
  );

  const sections = [];
  if (task.description) sections.push(
    h('div', { class: 'detail-section' },
      h('h4', {}, 'Açıklama'),
      h('div', { class: 'detail-text' }, task.description),
    ));
  if (task.note) sections.push(
    h('div', { class: 'detail-section' },
      h('h4', {}, 'Not'),
      h('div', { class: 'detail-text' }, task.note),
    ));
  if (task.postponed_reason) sections.push(
    h('div', { class: 'detail-section' },
      h('h4', {}, 'Erteleme nedeni'),
      h('div', { class: 'detail-text' }, task.postponed_reason),
    ));

  const meta = h('div', { class: 'detail-timestamps' },
    task.completed_at ? h('span', {}, 'Tamamlandı: ' + fmtDate(task.completed_at)) : null,
    task.updated_at ? h('span', {}, 'Güncellendi: ' + fmtDate(task.updated_at)) : null,
    task.created_at ? h('span', {}, 'Oluşturuldu: ' + fmtDate(task.created_at)) : null,
  );

  const actions = [];
  if (canComplete && !done && task.status !== 'postponed') {
    actions.push(h('button', {
      class: 'btn btn-primary',
      onclick: () => { closeModal(); openCompleteModal(task, onChange); },
    }, 'Tamamla'));
  }
  if (canEdit && done) {
    actions.push(h('button', {
      class: 'btn btn-secondary',
      onclick: () => { closeModal(); openCompleteModal(task, onChange); },
    }, 'Aktiviteyi düzenle'));
  }
  if (canEdit) {
    actions.push(h('button', {
      class: 'btn btn-secondary',
      onclick: () => { closeModal(); openTaskModal(task, onChange); },
    }, 'Düzenle'));
    actions.push(h('button', {
      class: 'btn btn-ghost',
      onclick: () => { closeModal(); openPostponeModal(task, onChange); },
    }, 'Ertele'));
  }
  actions.push(h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Kapat'));

  openModal({
    title: task.title,
    size: 'lg',
    body: h('div', { class: 'stack' },
      h('div', { class: 'detail-badges' },
        statusChip(task.status),
        h('span', { class: 'chip' }, 'Öncelik: ' + priorityLabel(task.priority)),
      ),
      grid,
      ...sections,
      meta,
    ),
    actions,
  });
}

export function openCompleteModal(task, onChange) {
  const isEditing = task.status === 'completed';
  const taskType = task.task_type || 'study';
  const isVideo = taskType === 'video';

  const am = h('input', { class: 'input', type: 'number', min: 0, step: 1,
    value: (isEditing ? task.actual_minutes : task.estimated_minutes) ?? '', placeholder: 'dk' });
  const qc = h('input', { class: 'input', type: 'number', min: 0, step: 1,
    value: (isEditing ? task.question_correct : '') ?? '', placeholder: 'doğru' });
  const qi = h('input', { class: 'input', type: 'number', min: 0, step: 1,
    value: (isEditing ? task.question_incorrect : '') ?? '', placeholder: 'yanlış' });
  const qb = h('input', { class: 'input', type: 'number', min: 0, step: 1,
    value: (isEditing ? task.question_blank : '') ?? '', placeholder: 'boş' });
  const nt = h('textarea', { class: 'textarea', placeholder: 'Nasıl geçti? (opsiyonel)' },
    isEditing ? (task.note || '') : '');

  // Canlı toplam + net göstergesi
  const netInfo = h('span', { class: 'hint', style: { fontWeight: 600 } }, '');
  const updateNet = () => {
    const c = Number(qc.value) || 0;
    const i = Number(qi.value) || 0;
    const b = Number(qb.value) || 0;
    if (c === 0 && i === 0 && b === 0 && qc.value === '' && qi.value === '' && qb.value === '') {
      netInfo.textContent = '';
      return;
    }
    netInfo.textContent = `Toplam ${c + i + b} · Net ${fmtNet(c, i)} · Boş ${b}`;
  };
  qc.addEventListener('input', updateNet);
  qi.addEventListener('input', updateNet);
  qb.addEventListener('input', updateNet);
  updateNet();

  const save = h('button', { class: 'btn btn-primary',
    onclick: async () => {
      save.disabled = true;
      try {
        const payload = {
          actual_minutes: am.value === '' ? null : Number(am.value),
          note: nt.value || null,
        };
        if (!isVideo) {
          payload.question_correct   = qc.value === '' ? null : Number(qc.value);
          payload.question_incorrect = qi.value === '' ? null : Number(qi.value);
          payload.question_blank     = qb.value === '' ? null : Number(qb.value);
        }
        await apiPost('/tasks/' + task.id + '/complete', payload);
        toast(isEditing ? 'Aktivite güncellendi' : 'Tamamlandı olarak işaretlendi', { type: 'success' });
        closeModal();
        onChange && onChange();
      } catch (err) {
        toast(err.message || 'Kaydedilemedi', { type: 'error' });
        save.disabled = false;
      }
    }
  }, 'Kaydet');

  const revoke = isEditing ? h('button', { class: 'btn btn-danger',
    onclick: () => confirmModal({
      title: 'Tamamlamayı geri al',
      message: '"' + task.title + '" tekrar bekliyor durumuna alınacak. Gerçekleşen süre, soru sayısı ve doğru/yanlış/boş sıfırlanır.',
      confirmText: 'Geri al', danger: true,
      onConfirm: async () => {
        try {
          await apiPatch('/tasks/' + task.id, {
            status: 'pending', actual_minutes: null,
            question_actual: null, question_correct: null, question_incorrect: null, question_blank: null,
          });
          toast('Tamamlama geri alındı', { type: 'success' });
          closeModal();
          onChange && onChange();
        } catch (err) { toast(err.message || 'Geri alınamadı', { type: 'error' }); }
      },
    }),
  }, 'Tamamlamayı geri al') : null;

  const typeLabel = taskTypeLabel(taskType);
  openModal({
    title: (isEditing ? 'Aktiviteyi düzenle · ' : 'Görevi tamamla · ') + task.title,
    body: h('div', { class: 'stack' },
      h('p', { class: 'muted' },
        h('span', { class: 'task-type-badge tt-' + taskType, style: { marginRight: '8px' } },
          taskTypeIcon(taskType), ' ', typeLabel),
        'Planlanan: ',
        task.estimated_minutes ? fmtDuration(task.estimated_minutes) : '—',
        task.question_target ? ' · ' + task.question_target + ' soru hedefi' : '',
      ),
      h('div', { class: 'form-grid' },
        h('div', { class: 'field' },
          h('label', {}, 'Gerçekleşen süre (dk)'),
          am,
          h('span', { class: 'hint' }, isEditing ? 'Boş bırakırsan sıfırlanır.' : 'Boş bırakırsan planlanan varsayılır.'),
        ),
        isVideo ? null : h('div', { class: 'field' },
          h('label', {}, 'Doğru'),
          qc,
        ),
        isVideo ? null : h('div', { class: 'field' },
          h('label', {}, 'Yanlış'),
          qi,
        ),
        isVideo ? null : h('div', { class: 'field' },
          h('label', {}, 'Boş'),
          qb,
        ),
      ),
      isVideo ? null : h('div', { class: 'field', style: { textAlign: 'right', marginTop: '-8px' } }, netInfo),
      h('div', { class: 'field' },
        h('label', {}, 'Not'),
        nt,
      ),
    ),
    actions: [
      h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'),
      revoke,
      save,
    ].filter(Boolean),
  });
}

export function openPostponeModal(task, onChange) {
  const dt = h('input', { class: 'input', type: 'date', value: task.task_date });
  const reason = h('input', { class: 'input', type: 'text', placeholder: 'Neden ertelendi?' });
  openModal({
    title: 'Görevi ertele',
    body: h('div', { class: 'stack' },
      h('div', { class: 'field' }, h('label', {}, 'Yeni tarih'), dt),
      h('div', { class: 'field' }, h('label', {}, 'Sebep (opsiyonel)'), reason),
    ),
    actions: [
      h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'),
      h('button', { class: 'btn btn-primary',
        onclick: async () => {
          try {
            await apiPost('/tasks/' + task.id + '/postpone', {
              new_date: dt.value, reason: reason.value || null,
            });
            toast('Ertelendi', { type: 'success' });
            closeModal();
            onChange && onChange();
          } catch (err) { toast(err.message, { type: 'error' }); }
        }
      }, 'Ertele'),
    ],
  });
}

export function openTaskModal(task, onChange, defaults = {}) {
  // Mola'yı düzenlerken kompakt mola modal'ına yönlendir
  if (task && Number(task.is_break)) return openBreakModal(task, onChange, defaults);
  const isEdit = !!task;
  const t = task || {};
  const initialTopicIds = Array.isArray(t.topic_ids) && t.topic_ids.length
    ? t.topic_ids.map(Number)
    : (t.topic_id ? [Number(t.topic_id)] : []);
  const state = {
    week_id: defaults.week_id ?? t.week_id ?? null,
    task_date: t.task_date ?? defaults.task_date ?? '',
    subject_id: t.subject_id ?? defaults.subject_id ?? '',
    topic_ids: new Set(initialTopicIds),
    title: t.title ?? '',
    description: t.description ?? '',
    planned_start: t.planned_start ? t.planned_start.slice(0,5) : (defaults.planned_start || ''),
    planned_end:   t.planned_end   ? t.planned_end.slice(0,5)   : '',
    estimated_minutes: t.estimated_minutes ?? '',
    question_target: t.question_target ?? '',
    priority: t.priority ?? 'normal',
    source: t.source ?? '',
    task_type: t.task_type ?? 'study',
    source_id: t.source_id ?? '',
  };

  const subjectSel = h('select', { class: 'select', required: true });
  const topicList = h('div', { class: 'topic-picker' });
  const titleIn    = h('input', { class: 'input', type: 'text', required: true, maxlength: 200, value: state.title });
  const dateIn     = h('input', { class: 'input', type: 'date', required: true, value: state.task_date });
  const sIn        = h('input', { class: 'input', type: 'time', value: state.planned_start });
  const eIn        = h('input', { class: 'input', type: 'time', value: state.planned_end });
  const estIn      = h('input', { class: 'input', type: 'number', min: 0, value: state.estimated_minutes });
  const qIn        = h('input', { class: 'input', type: 'number', min: 0, value: state.question_target });
  const priSel     = h('select', { class: 'select' },
    h('option', { value: 'low' }, 'Düşük'),
    h('option', { value: 'normal' }, 'Normal'),
    h('option', { value: 'high' }, 'Yüksek'),
    h('option', { value: 'critical' }, 'Kritik'),
  );
  priSel.value = state.priority;
  const descIn = h('textarea', { class: 'textarea', placeholder: 'Açıklama (opsiyonel)' }, state.description);

  // Görev türü — segmented control
  const typeGroup = h('div', { class: 'type-picker', role: 'radiogroup', 'aria-label': 'Görev türü' });
  const TYPES = [
    { v: 'study',    label: 'Konu Çalışması', icon: '📖' },
    { v: 'video',    label: 'Video',          icon: '▶'  },
    { v: 'practice', label: 'Soru Çözümü',    icon: '✍'  },
  ];
  const typeBtns = new Map();
  TYPES.forEach(({ v, label, icon }) => {
    const btn = h('button', {
      type: 'button',
      class: 'type-btn' + (state.task_type === v ? ' active' : ''),
      role: 'radio', 'aria-checked': state.task_type === v ? 'true' : 'false',
      onclick: () => setType(v),
    }, h('span', { class: 'type-icon' }, icon), h('span', { class: 'type-label' }, label));
    typeBtns.set(v, btn);
    typeGroup.appendChild(btn);
  });

  // Kaynak seçici (dropdown + "+ Yeni kaynak")
  const srcSel = h('select', { class: 'select' }, h('option', { value: '' }, '— Kaynak (opsiyonel) —'));
  const srcNew = h('button', {
    type: 'button', class: 'btn btn-ghost btn-sm',
    onclick: () => openSourceQuickModal(state.task_type, (created) => {
      state.source_id = created.id;
      loadSources(state.task_type).then(() => { srcSel.value = String(created.id); });
    }),
  }, '+ Yeni kaynak');

  let allSources = [];
  async function loadSources(taskType) {
    try {
      const { sources } = await apiGet('/sources');
      allSources = sources || [];
    } catch { allSources = []; }
    // Türe göre filtre: video → video+other; practice → question_bank+book+other; study → hepsi
    let filtered = allSources;
    if (taskType === 'video') filtered = allSources.filter(s => s.type === 'video' || s.type === 'other');
    if (taskType === 'practice') filtered = allSources.filter(s => s.type === 'question_bank' || s.type === 'book' || s.type === 'other');
    srcSel.replaceChildren(h('option', { value: '' }, '— Kaynak (opsiyonel) —'));
    filtered.forEach(s => srcSel.appendChild(h('option', { value: s.id }, `${sourceTypeLabel(s.type)} · ${s.name}`)));
    // Mevcut seçim listede yoksa boşalt
    if (state.source_id && filtered.some(s => Number(s.id) === Number(state.source_id))) {
      srcSel.value = String(state.source_id);
    } else if (state.source_id) {
      srcSel.value = '';
      state.source_id = '';
    }
  }
  srcSel.addEventListener('change', () => { state.source_id = srcSel.value; });

  // Video ise soru hedefi alanını gizle
  const qField = h('div', { class: 'field' }, h('label', {}, 'Soru hedefi'), qIn);

  function setType(v) {
    state.task_type = v;
    typeBtns.forEach((btn, key) => {
      const active = key === v;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
    });
    qField.style.display = (v === 'video') ? 'none' : '';
    if (v === 'video') qIn.value = '';
    loadSources(v);
  }
  loadSources(state.task_type);
  // İlk render: video ise gizle
  if (state.task_type === 'video') qField.style.display = 'none';

  // Populate subjects
  apiGet('/subjects').then(({ subjects }) => {
    subjectSel.replaceChildren(h('option', { value: '' }, '— Ders seç —'));
    subjects.forEach(s => subjectSel.appendChild(h('option', { value: s.id }, s.name)));
    subjectSel.value = state.subject_id || '';
    if (subjectSel.value) loadTopics(Number(subjectSel.value));
  });

  async function loadTopics(subjectId) {
    if (!subjectId) {
      topicList.replaceChildren(h('p', { class: 'subtle', style: { padding: 'var(--sp-2) 0' } }, 'Önce bir ders seç.'));
      return;
    }
    topicList.replaceChildren(h('p', { class: 'subtle', style: { padding: 'var(--sp-2) 0' } }, 'Konular yükleniyor…'));
    const { topics } = await apiGet('/topics?subject_id=' + subjectId);
    topicList.replaceChildren();
    if (topics.length === 0) {
      topicList.appendChild(h('p', { class: 'subtle', style: { padding: 'var(--sp-2) 0' } },
        'Bu derste henüz konu yok. Dersler & Konular sayfasından ekleyebilirsin.'));
      return;
    }
    // Ders değişince önceki seçim geçersiz — sadece bu dersin konularını tut
    const allowed = new Set(topics.map(tp => Number(tp.id)));
    for (const id of Array.from(state.topic_ids)) {
      if (!allowed.has(id)) state.topic_ids.delete(id);
    }
    topics.forEach(tp => {
      const cb = h('input', {
        type: 'checkbox', value: tp.id,
        checked: state.topic_ids.has(Number(tp.id)),
        onchange: (e) => {
          const id = Number(tp.id);
          if (e.target.checked) state.topic_ids.add(id);
          else state.topic_ids.delete(id);
          updateChipRow();
        },
      });
      topicList.appendChild(h('label', { class: 'topic-option' }, cb, h('span', {}, tp.name)));
    });
    updateChipRow();
  }
  subjectSel.addEventListener('change', () => loadTopics(Number(subjectSel.value)));

  // Seçilen konular — özet chip satırı
  const chipRow = h('div', { class: 'topic-chips' });
  function updateChipRow() {
    const selectedNames = Array.from(topicList.querySelectorAll('input:checked'))
      .map(inp => inp.closest('label').querySelector('span').textContent);
    chipRow.replaceChildren();
    if (selectedNames.length === 0) {
      chipRow.appendChild(h('span', { class: 'subtle' }, 'Henüz konu seçilmedi (opsiyonel).'));
      return;
    }
    selectedNames.forEach(n => chipRow.appendChild(h('span', { class: 'chip' }, n)));
  }
  updateChipRow();

  const save = h('button', { class: 'btn btn-primary',
    onclick: async () => {
      save.disabled = true;
      try {
        const payload = {
          title: titleIn.value.trim(),
          subject_id: Number(subjectSel.value) || null,
          topic_ids: Array.from(state.topic_ids),
          task_date: dateIn.value,
          planned_start: sIn.value || null,
          planned_end: eIn.value || null,
          estimated_minutes: estIn.value === '' ? null : Number(estIn.value),
          question_target: state.task_type === 'video' ? null : (qIn.value === '' ? null : Number(qIn.value)),
          priority: priSel.value,
          source_id: state.source_id ? Number(state.source_id) : null,
          task_type: state.task_type,
          description: descIn.value || null,
        };
        if (!isEdit) payload.week_id = state.week_id;
        if (isEdit) {
          await apiPatch('/tasks/' + t.id, payload);
          toast('Görev güncellendi', { type: 'success' });
        } else {
          await apiPost('/tasks', payload);
          toast('Görev eklendi', { type: 'success' });
        }
        closeModal();
        onChange && onChange();
      } catch (err) {
        const details = err.details ? Object.values(err.details).join(' ') : '';
        toast((err.message || 'Kaydedilemedi') + ' ' + details, { type: 'error' });
        save.disabled = false;
      }
    }
  }, isEdit ? 'Değişiklikleri kaydet' : 'Görevi ekle');

  openModal({
    title: isEdit ? 'Görevi düzenle' : 'Yeni görev',
    size: 'lg',
    body: h('div', { class: 'stack' },
      h('div', { class: 'field' }, h('label', {}, 'Görev türü'), typeGroup),
      h('div', { class: 'field' }, h('label', {}, 'Başlık'), titleIn),
      h('div', { class: 'form-grid' },
        h('div', { class: 'field' }, h('label', {}, 'Ders'), subjectSel),
        h('div', { class: 'field' }, h('label', {}, 'Tarih'), dateIn),
        h('div', { class: 'field' }, h('label', {}, 'Öncelik'), priSel),
        h('div', { class: 'field' }, h('label', {}, 'Başlangıç'), sIn),
        h('div', { class: 'field' }, h('label', {}, 'Bitiş'), eIn),
        h('div', { class: 'field' }, h('label', {}, 'Tahmini süre (dk)'), estIn),
        qField,
      ),
      h('div', { class: 'field' },
        h('label', {}, 'Konular'),
        h('span', { class: 'hint' }, 'Aynı dersten birden fazla konu seçebilirsin.'),
        chipRow,
        topicList,
      ),
      h('div', { class: 'field' },
        h('label', {}, 'Kaynak'),
        h('div', { class: 'row' }, srcSel, srcNew),
        h('span', { class: 'hint' }, 'Kaynaklar sayfasından yönetebilirsin.'),
      ),
      h('div', { class: 'field' }, h('label', {}, 'Açıklama'), descIn),
    ),
    actions: [
      h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'),
      save,
    ],
  });
}

// Yeni kaynağı hızla ekle — görev formu içinden çağrılır.
export function openSourceQuickModal(preselectTaskType, onCreated) {
  const preselectType = preselectTaskType === 'video' ? 'video'
                     : preselectTaskType === 'practice' ? 'question_bank'
                     : 'other';
  const name = h('input', { class: 'input', type: 'text', maxlength: 150, required: true, placeholder: 'Örn. Karekök TYT Denemesi' });
  const typeSel = h('select', { class: 'select' },
    h('option', { value: 'video' }, 'Video'),
    h('option', { value: 'question_bank' }, 'Soru Bankası'),
    h('option', { value: 'book' }, 'Kitap'),
    h('option', { value: 'other' }, 'Diğer'),
  );
  typeSel.value = preselectType;
  const publisher = h('input', { class: 'input', type: 'text', maxlength: 120, placeholder: 'Kanal / yayınevi' });
  const url = h('input', { class: 'input', type: 'url', maxlength: 500, placeholder: 'https://…' });

  const save = h('button', { class: 'btn btn-primary',
    onclick: async () => {
      save.disabled = true;
      try {
        const nm = name.value.trim();
        if (!nm) { toast('Ad zorunlu', { type: 'error' }); save.disabled = false; return; }
        const res = await apiPost('/sources', {
          name: nm, type: typeSel.value,
          publisher: publisher.value || null, url: url.value || null,
        });
        toast('Kaynak eklendi', { type: 'success' });
        closeModal();
        onCreated && onCreated({ id: res.id, name: nm, type: typeSel.value });
      } catch (err) {
        toast(err.message || 'Kaydedilemedi', { type: 'error' });
        save.disabled = false;
      }
    },
  }, 'Ekle');

  openModal({
    title: 'Yeni kaynak',
    body: h('div', { class: 'stack' },
      h('div', { class: 'field' }, h('label', {}, 'Ad'), name),
      h('div', { class: 'form-grid' },
        h('div', { class: 'field' }, h('label', {}, 'Tür'), typeSel),
        h('div', { class: 'field' }, h('label', {}, 'Kanal / yayınevi'), publisher),
      ),
      h('div', { class: 'field' }, h('label', {}, 'Bağlantı'), url),
    ),
    actions: [
      h('button', { class: 'btn btn-ghost', onclick: closeModal }, 'Vazgeç'),
      save,
    ],
  });
}
